import type { QueryKey } from '@tanstack/react-query';

import {
  apiListQuery,
  apiQuery,
  useApiMutation,
  type ApiMutationOptions,
  type ApiMutationResult,
} from '@/shared/data';
import { getCurrentTenant } from '@/shared/http';

import * as api from './billing-setting.api';
import { billingSettingKeys } from './billing-setting.keys';
import type { BillingSettingBody, BillingSettingListParams } from './billing-setting.wire';

// Cache e mutações da Régua de cobrança (shared/data). Sem texto de UI: o toast é da tela (X06).

export { billingSettingKeys };

/** A lista manda `tenant` (billingParametersContext.tsx:39): ele já está nos params da chave. */
export const billingSettingListQuery = (params: BillingSettingListParams) =>
  apiListQuery(billingSettingKeys.list(params), ({ signal }) =>
    api.listBillingSettings(params, signal),
  );

/** Porta F0: opções de régua para outros módulos (a tela não usa). */
export const billingSettingOptionsQuery = (
  params: {
    searchString?: string | undefined;
    walletsCode?: string | number | null | undefined;
    tenant?: string | undefined;
  },
  extra: { enabled?: boolean } = {},
) => {
  const resolved = { ...params, tenant: 'tenant' in params ? params.tenant : getCurrentTenant() };
  return apiListQuery(
    billingSettingKeys.custom('options', resolved),
    ({ signal }) => api.listBillingSettingOptions(resolved, signal),
    extra,
  );
};

export const billingSettingOptionQuery = (id: string) =>
  apiQuery(
    billingSettingKeys.custom('option', id),
    ({ signal }) => api.getBillingSetting(id, signal),
    { enabled: id !== '' },
  );

// ─── mutações (X07: um hook genérico, tipado pelo nome) ────────────────────

interface MutationDef<V, D> {
  readonly run: (variables: V) => Promise<D>;
  readonly invalidate: readonly QueryKey[];
}

export const billingSettingMutations = {
  create: {
    run: ({ body }: { body: BillingSettingBody }) => api.createBillingSetting(body),
    invalidate: [billingSettingKeys.all],
  },
  update: {
    run: ({ id, body }: { id: string; body: BillingSettingBody }) =>
      api.updateBillingSetting(id, body),
    invalidate: [billingSettingKeys.all],
  },
  delete: {
    run: ({ id }: { id: string }) => api.deleteBillingSetting(id),
    invalidate: [billingSettingKeys.all],
  },
} as const satisfies Record<string, MutationDef<never, unknown>>;

export type BillingSettingMutationName = keyof typeof billingSettingMutations;
type Def<K extends BillingSettingMutationName> = (typeof billingSettingMutations)[K];
export type BillingSettingMutationVariables<K extends BillingSettingMutationName> = Parameters<
  Def<K>['run']
>[0];
export type BillingSettingMutationData<K extends BillingSettingMutationName> = Awaited<
  ReturnType<Def<K>['run']>
>;

/**
 * Mutação de uma escrita do fio. Invalida o módulo depois do sucesso; o feedback é da tela (X06):
 * passe `success: false, error: false` quando o sheet ou o diálogo mostra o erro (X11).
 */
export function useBillingSettingMutation<K extends BillingSettingMutationName>(
  name: K,
  options: Omit<
    ApiMutationOptions<BillingSettingMutationData<K>, BillingSettingMutationVariables<K>>,
    'mutationFn'
  > = {},
): ApiMutationResult<BillingSettingMutationData<K>, BillingSettingMutationVariables<K>> {
  const def = billingSettingMutations[name] as unknown as MutationDef<
    BillingSettingMutationVariables<K>,
    BillingSettingMutationData<K>
  >;
  return useApiMutation<BillingSettingMutationData<K>, BillingSettingMutationVariables<K>>({
    mutationKey: ['billing-setting', 'mutation', name],
    invalidate: def.invalidate,
    ...options,
    mutationFn: def.run,
  });
}

export const billingSettingQuery = (id: string | null | undefined) =>
  billingSettingOptionQuery(id ?? '');
